import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATION, RouterNavigationAction } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { Company, UserInfo, consumePendingCompanyContext } from 'app/shared/models';
import { AttributeService, CompanyService, SurveyService } from 'app/shared/services';
import * as CompanyContextActions from 'app/state/actions/company-context.actions';
import * as NavTreeAction from 'app/state/actions/navtree.actions';
import * as UserInfoActions from 'app/state/actions/user-info.actions';
import * as OrganizationActions from 'app/state/actions/organization.actions';
import { AppState } from 'app/state/app.state';
import { from } from 'rxjs';
import { combineLatestWith, concatMap, filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { selectUserInfo } from '../selectors/user-info.selectors';

@Injectable()
export class CompanyContextEffects {
  private actions$ = inject(Actions);
  private companyService = inject(CompanyService);
  private surveyService = inject(SurveyService);
  private attributeService = inject(AttributeService);
  private store = inject<Store<AppState>>(Store);


  setCompanyContextSuccess = createEffect(() => this.actions$.pipe(
    ofType<CompanyContextActions.SetCompanyContextSuccess | CompanyContextActions.ContextAlreadySet>(CompanyContextActions.Type.SET_COMPANY_CONTEXT_SUCCESS, CompanyContextActions.Type.CONTEXT_ALREADY_SET),
    withLatestFrom(this.store.select(s => s.userInfo)),
    filter(([company, userInfo]) => !!(userInfo.displayLanguageId) && company.payload.companyLanguages?.length > 0),
    switchMap(([company, userInfo]) => {

      const companyLanguages = company.payload.companyLanguages;
      const displayLangId = userInfo.displayLanguageId;
      const userLanguageId = userInfo.languageId;
      const lang = companyLanguages.find(cl => cl.id === (userLanguageId)) ?? companyLanguages.find(cl => cl.id === displayLangId) ?? companyLanguages.find(cl => cl.isDefaultCompanyLanguage);
      /*  When company context has switched, we check if our current display Language is among the context company languages
      //  If that is the case, we can just reload the navigation
      */
      return [ new UserInfoActions.SetUserContextLanguage(lang), new NavTreeAction.LoadNavtree(), OrganizationActions.resetState()];
    })
  ));

  saveContextToLocalStorage$ = createEffect(() => this.actions$.pipe(
    ofType<CompanyContextActions.SetCompanyContextSuccess>(CompanyContextActions.Type.SET_COMPANY_CONTEXT_SUCCESS),
    withLatestFrom(
      this.store.select(selectUserInfo)
    ),
    switchMap(([companyContext, userInfo]) => {
      this.setSavedCompany(companyContext.payload.id, companyContext.payload.shortName,  userInfo.userId, companyContext.payload.productContext.id);
      return from([])
    })
  ));

  setCompanyContext$ = createEffect(() => this.actions$.pipe(
    ofType<CompanyContextActions.SetCompanyContext | CompanyContextActions.SetCompanyContextByShortName>(
      CompanyContextActions.Type.SET_COMPANY_CONTEXT, CompanyContextActions.Type.SET_COMPANY_CONTEXT_BY_SHORT_NAME),
    withLatestFrom(this.store.select(state => state.companyContext), this.store.select(state => state.userInfo)),
    switchMap(([action, companyContext, userInfo]) => {
      if (!action.force && (action.payload.companyId === companyContext.id || action.payload.shortName === companyContext.shortName)) {

        // We are in the same company, but also check if context is the same
        if (action.payload.productContextId && action.payload.productContextId === companyContext.productContext.id) {
          return from([
            new UserInfoActions.LoadUserInfoSuccess(userInfo),
            new CompanyContextActions.SetCompanyContextSuccess(companyContext),
            ...action.onSuccess
          ]);
        }

      }
      let companyId: string | number;
      let productContextId: number = null;

      const localStorageInfo = JSON.parse(this.getSavedCompany()) as { companyId: number, shortName: string, userId: number, productContextId: number };
      if (!isNaN(+action.payload.companyId)) { // case when reloading without companyName in url
        companyId = +action.payload.companyId;
        if (!action.force && !companyContext.id && localStorageInfo?.companyId > 0 && userInfo.userId === localStorageInfo?.userId) {
          companyId = localStorageInfo.companyId;
          productContextId = localStorageInfo.productContextId
        }
      } else if (!action.force && action.payload.shortName === localStorageInfo.shortName) {
        // We are on the same company (by url) but make sure to load the right product context
        companyId = localStorageInfo.companyId;
        productContextId = localStorageInfo.productContextId
      }

      if (action.payload.productContextId) productContextId = action.payload.productContextId;

      return this.companyService.get(companyId || action.payload.companyId, productContextId).pipe(
        concatMap(company => [
          userInfo.userPermission.companyId !== company.id
          ? new UserInfoActions.LoadUserInfo(company.id)
            : new UserInfoActions.LoadUserInfoSuccess(userInfo),
          new CompanyContextActions.SetCompanyContextSuccess(company),
          ...action.onSuccess
        ])
      );
    }
    )
  ));

  setContextByToken$ = createEffect(() => this.actions$.pipe(
    ofType<CompanyContextActions.SetCompanyContextByToken>(CompanyContextActions.Type.SET_COMPANY_CONTEXT_BY_TOKEN),
    switchMap((action) =>
      this.surveyService.getContext(action.payload.token)
    )
  ).pipe(
    switchMap(context => from([
      new UserInfoActions.LoadUserInfoSuccess(context.userContext),
      new CompanyContextActions.SetCompanyContextSuccess({ ...context.companyContext, token: 'abc123' })
    ])))
  );

  useRouteContext$ = createEffect(() => this.actions$.pipe(
    ofType<RouterNavigationAction>(ROUTER_NAVIGATION),
    combineLatestWith(
      this.actions$.pipe(ofType<UserInfoActions.LoadUserInfoSuccess>(UserInfoActions.Type.LOAD_USER_INFO_SUCCESS))
    ),
    withLatestFrom(this.store.select(state => state.companyContext)),
    map(([[routerNavigationAction, userInfo], companyContext]): [Record<string, string>, UserInfo, Company] =>
      ([this.getRouteParams(routerNavigationAction.payload.routerState.root), userInfo.payload, companyContext])),
    filter(([params, userInfo, _]) => userInfo.companyId !== null || 'companyId' in params || 'companyShortName' in params),
    take(1),
    map(([params, userInfo, companyContext]) => {
      let companyId = +{ ...userInfo }.companyId;
      
      const pending = consumePendingCompanyContext();
      if (pending?.companyId > 0) {
        return new CompanyContextActions.SetCompanyContext(
          { companyId: pending.companyId, productContextId: pending.productContextId }, [], true);
      }

      if ('companyShortName' in params) {
        return new CompanyContextActions.SetCompanyContextByShortName( { shortName: params.companyShortName });
      }

      if ('companyId' in params) {
        companyId = +params.companyId;
      }
      return companyContext?.id === companyId ? new CompanyContextActions.ContextAlreadySet(companyContext) : new CompanyContextActions.SetCompanyContext({ companyId: companyId });
    })
  ));

  saveCompanyAttributes$ = createEffect(() => this.actions$.pipe(
    ofType<CompanyContextActions.SaveCompanyAttributes>(CompanyContextActions.Type.SAVE_COMPANY_ATTRIBUTES),
    withLatestFrom(this.store.select(state => state.companyContext.id)),
    switchMap(([action, companyId]) =>
      this.attributeService.updateCompanyMapped(companyId, action.payload)),
    switchMap((res) => from([new CompanyContextActions.SaveCompanyAttributesSuccess(res)]))
  ));

  private getRouteParams(routeData: ActivatedRouteSnapshot) {
    let result = { ...routeData.params };
    while ('firstChild' in routeData && routeData.firstChild) {
      routeData = routeData.firstChild;
      result = { ...result, ...routeData.params };
    }
    return result;
  }

  private setSavedCompany(companyId: number, shortName: string, userId: number, productContextId: number) {
    localStorage.setItem('currentCompanyIdAndUserIdAndProductContext', JSON.stringify({ companyId: companyId, shortName: shortName, userId: userId, productContextId: productContextId }));
  }

  private getSavedCompany() {
    return localStorage.getItem('currentCompanyIdAndUserIdAndProductContext');
  }
}
